import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { AnalyticsProvider, useAnalytics } from './context/AnalyticsContext';
import { WishlistProvider } from './context/WishlistContext';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { CookieConsent } from './components/layout/CookieConsent';
import { Loader2 } from 'lucide-react';
import { parseCategorySlug } from './lib/urlHelpers';

// Eagerly loaded (above the fold)
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import AuthPage from './pages/AuthPage';

// Lazy loaded (below the fold / secondary pages)
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const PCBuilderPage = lazy(() => import('./pages/PCBuilderPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const MockPaymentPage = lazy(() => import('./pages/MockPaymentPage'));
const CheckoutResultPage = lazy(() => import('./pages/CheckoutResultPage'));
const ImpresszumPage = lazy(() => import('./pages/ImpresszumPage'));
const AszfPage = lazy(() => import('./pages/AszfPage'));
const AdatkezelesPage = lazy(() => import('./pages/AdatkezelesPage'));
const CookiePage = lazy(() => import('./pages/CookiePage'));
const GaranciaPage = lazy(() => import('./pages/GaranciaPage'));
const SzallitasPage = lazy(() => import('./pages/SzallitasPage'));
const VisszakuldesPage = lazy(() => import('./pages/VisszakuldesPage'));
const SharedBuildPage = lazy(() => import('./pages/SharedBuildPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));

import { SupportButton } from './components/SupportButton';
import './App.css';

// Resolves /products/:categorySlug to ProductsPage with proper filter params
function CategoryRedirect() {
    var { categorySlug } = useParams();
    var mapped = parseCategorySlug(categorySlug);
    if (!mapped) return <ProductsPage />;
    // Create a URLSearchParams-compatible object for ProductsPage
    var params = new URLSearchParams();
    if (mapped.category) params.set('category', mapped.category);
    if (mapped.subcategory) params.set('subcategory', mapped.subcategory);
    if (mapped.condition) params.set('condition', mapped.condition);
    return <ProductsPage overrideParams={params} key={categorySlug} />;
}

function PageLoader() {
    return (
        <div className="flex items-center justify-center min-h-[50vh]">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
    );
}

// Scroll to top on route change and track page views
function ScrollToTop() {
    const { pathname } = useLocation();
    const { trackPageView } = useAnalytics();
    
    useEffect(() => {
        window.scrollTo(0, 0);
        trackPageView(pathname);
    }, [pathname, trackPageView]);
    
    return null;
}

function AppContent() {
    const { theme } = useTheme();
    const location = useLocation();
    const isHomePage = location.pathname === '/';
    
    const isMockPayment = location.pathname === '/mock-payment';
    
    if (isMockPayment) {
        return <MockPaymentPage />;
    }

    return (
        <div className="flex flex-col min-h-screen relative bg-background">
            {/* Subtle gradient blobs - hidden on homepage */}
            {!isHomePage && (
                <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-primary/20 dark:bg-primary/30 rounded-full blur-3xl" />
                    <div className="absolute top-1/3 -left-40 w-[400px] h-[400px] bg-cyan-500/15 dark:bg-cyan-500/20 rounded-full blur-3xl" />
                    <div className="absolute -bottom-40 right-1/4 w-[500px] h-[500px] bg-primary/15 dark:bg-primary/25 rounded-full blur-3xl" />
                    <div className="absolute bottom-1/4 left-1/3 w-[300px] h-[300px] bg-blue-500/10 dark:bg-blue-500/15 rounded-full blur-3xl" />
                </div>
            )}
            
            <ScrollToTop />
            <Navbar />
            <main className="flex-1 relative z-10">
                <Suspense fallback={<PageLoader />}>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/products" element={<ProductsPage />} />
                    <Route path="/products/:categorySlug" element={<CategoryRedirect />} />
                    <Route path="/termek/:slug" element={<ProductDetailPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/auth" element={<AuthPage />} />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/pc-builder" element={<PCBuilderPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/checkout/result" element={<CheckoutResultPage />} />
                    <Route path="/impresszum" element={<ImpresszumPage />} />
                    <Route path="/aszf" element={<AszfPage />} />
                    <Route path="/adatkezeles" element={<AdatkezelesPage />} />
                    <Route path="/cookie" element={<CookiePage />} />
                    <Route path="/garancia" element={<GaranciaPage />} />
                    <Route path="/szallitas" element={<SzallitasPage />} />
                    <Route path="/visszakuldes" element={<VisszakuldesPage />} />
                    <Route path="/osszeallitas/:shareId" element={<SharedBuildPage />} />
                    <Route path="/mock-payment" element={<MockPaymentPage />} />
                </Routes>
                </Suspense>
            </main>
            <Footer />
            <CookieConsent />
            <SupportButton />
            <Toaster 
                position="bottom-right" 
                theme={theme}
                toastOptions={{
                    style: {
                        background: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        color: 'hsl(var(--foreground))',
                    },
                }}
            />
        </div>
    );
}

function App() {
    return (
        <BrowserRouter>
            <ThemeProvider>
                <AuthProvider>
                    <CartProvider>
                        <WishlistProvider>
                            <AnalyticsProvider>
                                <AppContent />
                            </AnalyticsProvider>
                        </WishlistProvider>
                    </CartProvider>
                </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
